import Layout from '@eduzz/houston-ui/Layout';
import Avatar from '@material-ui/core/Avatar';
import Icon from '@material-ui/core/Icon';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import PermissionHide from 'components/PermissionHide';
import Truncate from 'components/Truncate';
import React, { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useObservable } from 'react-use-observable';
import { VERTICAL_URL } from 'settings';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import type { IAccess } from 'v2/interfaces/account';

const { Topbar } = Layout;

const useStyles = makeStyles(() => ({
  avatar: {
    width: 28,
    height: 28,
    fontSize: 16,
    fontWeight: 600,
  },
}));

const ProfileMenu = () => {
  const classes = useStyles();

  const [isLoading] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []) ?? [];

  const handleClickLogout = useCallback(() => eduzzAccountsService.logout(), []);

  const handleClickChangeAccount = useCallback((access: IAccess) => eduzzAccountsService.changeAccount(access.email), []);

  if (!user) {
    return null;
  }

  const isUnity = user.is_clube_black;

  return (
    <Topbar.UserMenu id='topbar-user-menu'>
      <PermissionHide>
        {user.general_term && (
          <Topbar.UserMenuItem
            id='topbar-user-menu-item-bank-data'
            as={NavLink}
            to='/producer/profile/bankdata'
            icon={<Icon>account_balance_wallet</Icon>}
          >
            Dados Bancários
          </Topbar.UserMenuItem>
        )}

        <Topbar.UserMenuItem
          id='topbar-user-menu-item-profile-data'
          icon={<Icon>account_circle</Icon>}
          {...(isUnity
            ? {
                as: 'a',
                href: `${VERTICAL_URL}/profile`,
              }
            : {
                as: NavLink,
                to: '/producer/profile/edit',
              })}
        >
          Dados Cadastrais
        </Topbar.UserMenuItem>

        <Topbar.UserMenuItem id='topbar-user-menu-item-payment-methods' as={NavLink} to='/producer/profile/payment' icon={<Icon>credit_card</Icon>}>
          Minhas Formas de Pagamento
        </Topbar.UserMenuItem>

        <Topbar.UserMenuDivider />
      </PermissionHide>

      {user.general_term && (
        <Topbar.UserMenuItem id='topbar-user-menu-item-purchases' as={NavLink} to='/producer/shopping' icon={<Icon>shopping_cart</Icon>}>
          Minhas Compras
        </Topbar.UserMenuItem>
      )}

      {user.acceptence_terms && (
        <Topbar.UserMenuItem id='topbar-user-menu-item-dashboard-elite' as={NavLink} to='/producer/elite' icon={<Icon>grade</Icon>}>
          Dashboard Elite
        </Topbar.UserMenuItem>
      )}

      {!!user.is_clube_black && (
        <Topbar.UserMenuItem
          id='topbar-user-menu-item-unity'
          as={NavLink}
          to={{ pathname: 'https://unity.eduzzlabs.com/' }}
          target='_blank'
          rel='noopener noreferrer'
          icon={<Icon>verified_user</Icon>}
        >
          Unity
        </Topbar.UserMenuItem>
      )}

      {accesses?.length > 1 && (
        <>
          <Topbar.UserMenuDivider />

          <Topbar.UserMenuGroup label='Contas:'>
            {accesses.map((access, index) => {
              const disableTooltip = access.termsAcceptance || access.isMe;
              const name = access?.name?.trim()?.substr(0, 1);

              return (
                <Tooltip
                  arrow
                  disableFocusListener={disableTooltip}
                  disableHoverListener={disableTooltip}
                  disableTouchListener={disableTooltip}
                  title={
                    <Typography variant='body2'>
                      O titular desta conta ainda não aceitou a atualização dos nossos Termos de Uso. Solicite a confirmação para o responsável e
                      assim que ele a fizer o seu acesso será liberado. Em caso de dúvidas entre em contato com o nosso suporte: suporte@eduzz.com
                    </Typography>
                  }
                  placement='left'
                  key={access.id}
                >
                  <Topbar.UserMenuItem
                    id={`top-user-menu-item-account-${index}`}
                    onClick={() => handleClickChangeAccount(access)}
                    disabled={isLoading || (!access.termsAcceptance && !access.isMe)}
                    icon={
                      <Avatar className={classes.avatar} src={access?.photo}>
                        {name}
                      </Avatar>
                    }
                  >
                    <Truncate count={25} final='...'>
                      {access?.name}
                    </Truncate>
                  </Topbar.UserMenuItem>
                </Tooltip>
              );
            })}
          </Topbar.UserMenuGroup>
        </>
      )}

      <Topbar.UserMenuDivider />

      <PermissionHide action='myeduzz:ajuda:visualizar'>
        <Topbar.UserMenuItem id='topbar-user-menu-item-exit' as={NavLink} to='/producer/help' icon={<Icon>help</Icon>}>
          Ajuda
        </Topbar.UserMenuItem>
      </PermissionHide>

      <Topbar.UserMenuItem id='topbar-user-menu-item-exit' icon={<Icon>exit_to_app</Icon>} onClick={handleClickLogout}>
        Sair
      </Topbar.UserMenuItem>
    </Topbar.UserMenu>
  );
};

export default ProfileMenu;
